import { useEffect, useMemo, useState, type ReactNode } from "react";
import { AlertTriangle, MessageCircle, Pause, PhoneCall, Play, RefreshCw, Send } from "lucide-react";
import type { Case } from "@/lib/useRealtimeCases";
import { ReminderProgressBar } from "@/components/kanban/ReminderProgressBar";
import { SectionTitle } from "./SectionTitle";
import {
  OWNER_META,
  availableActions,
  caseProgress,
  formatDateTime,
  formatMoney,
  maxQuoteAmount,
  nextStepText,
  ownerOf,
  stepRows,
  unresolvedFailures,
  whatsappRow,
  type OwnerKey,
  type StepState,
} from "./caseRelances";
import type { CaseRelancesState, RelanceAction } from "./useCaseRelances";

const OWNER_TONE: Record<OwnerKey, { box: string; dot: string; text: string }> = {
  you: { box: "border-orange-200 bg-orange-50/60", dot: "bg-orange-500", text: "text-orange-900" },
  running: { box: "border-indigo-200 bg-indigo-50/50", dot: "bg-indigo-600", text: "text-indigo-900" },
  stopped: { box: "border-gray-200 bg-gray-50", dot: "bg-gray-400", text: "text-gray-900" },
  error: { box: "border-red-200 bg-red-50/60", dot: "bg-red-600", text: "text-red-900" },
};

const STEP_DOT: Record<StepState, string> = {
  sent: "border-indigo-600 bg-indigo-600 text-white",
  failed: "border-red-600 bg-red-600 text-white",
  next: "border-indigo-600 bg-white text-indigo-700",
  upcoming: "border-gray-300 bg-white text-gray-500",
  cancelled: "border-gray-200 bg-gray-50 text-gray-400",
};
const STEP_TEXT: Record<StepState, string> = {
  sent: "text-gray-600",
  failed: "text-red-700",
  next: "text-indigo-700 font-medium",
  upcoming: "text-gray-500",
  cancelled: "text-gray-400",
};

const DONE_MESSAGES: Record<RelanceAction, string> = {
  call: "Appel noté : les relances sont arrêtées.",
  pause: "Relances mises en pause.",
  resume: "Relances reprises : prochaine dans 48 h (jours ouvrés, 9 h – 18 h).",
  "send-now": "Relance suivante programmée : elle part au prochain créneau ouvré.",
  retry: "Nouvel essai programmé : la relance part au prochain créneau ouvré.",
};

const btn =
  "inline-flex min-h-[36px] items-center justify-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium leading-tight focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50";
const primary = `${btn} bg-gray-900 text-white hover:bg-gray-800`;
const secondary = `${btn} border border-gray-300 bg-white text-gray-700 hover:bg-gray-50`;

export function RulesBanner() {
  const enabled = import.meta.env.VITE_QUOTE_REMINDERS_ENABLED === "true";
  return (
    <div
      role="note"
      aria-label="Règles des relances"
      className={`mt-4 rounded-md border px-3 py-2 text-[11px] leading-5 ${enabled ? "border-gray-200 bg-white text-gray-600" : "border-amber-200 bg-amber-50 text-amber-900"}`}
    >
      <span className="inline-flex items-center gap-1.5 font-semibold">
        <span className={`h-1.5 w-1.5 rounded-full ${enabled ? "bg-green-600" : "bg-amber-500"}`} aria-hidden="true" />
        {enabled ? "Envois automatiques actifs" : "Envois automatiques suspendus"}
      </span>
      <span className="block">
        En semaine 9 h – 18 h · 4 relances J+2, J+4, J+6, J+8 · arrêt dès qu’un humain agit (e-mail, appel, carte déplacée).
      </span>
    </div>
  );
}

interface Props {
  caseData: Case;
  state: CaseRelancesState;
}

/** Bloc « Relances devis » : qui a la main, barre R1-R4, erreurs, actions, calendrier. */
export function RelancesDevisBlock({ caseData, state }: Props) {
  const { cycle, logs, quotes, loading, error, actionError, busyAction, run, reload } = state;
  const [done, setDone] = useState<string | null>(null);
  useEffect(() => setDone(null), [caseData.id]);

  const amountHt = useMemo(() => maxQuoteAmount(quotes), [quotes]);
  const view = useMemo(() => (cycle ? caseProgress(caseData, cycle, amountHt) : null), [caseData, cycle, amountHt]);
  const failures = useMemo(() => unresolvedFailures(logs), [logs]);
  const titleId = `relances-devis-title-${caseData.id}`;

  const act = async (action: RelanceAction) => {
    setDone(null);
    if (await run(action)) setDone(DONE_MESSAGES[action]);
  };

  let body: ReactNode;
  if (loading) {
    body = (
      <div className="mt-3 space-y-2" aria-busy="true" aria-label="Chargement des relances">
        <div className="h-16 animate-pulse rounded-md bg-gray-100" />
        <div className="h-9 animate-pulse rounded-md bg-gray-100" />
        <div className="h-24 animate-pulse rounded-md bg-gray-100" />
      </div>
    );
  } else if (error) {
    body = (
      <div role="alert" className="mt-3 rounded-md border border-red-200 bg-red-50 p-3 text-xs text-red-800">
        <p className="font-semibold">Relances indisponibles</p>
        <p className="mt-0.5 break-words">{error}</p>
        <button type="button" onClick={() => void reload()} className={`${secondary} mt-2`}>
          <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />
          Réessayer
        </button>
      </div>
    );
  } else if (!cycle || !view) {
    body = (
      <div className="mt-3 rounded-md border border-dashed border-gray-300 px-3 py-4 text-center">
        <p className="text-sm font-medium text-gray-800">Aucune relance devis</p>
        <p className="mt-1 text-xs leading-5 text-gray-500">
          Les relances démarrent toutes seules quand un devis est envoyé au client depuis Messages.
        </p>
      </div>
    );
  } else {
    const owner = ownerOf(view);
    const tone = OWNER_TONE[owner];
    const actions = availableActions(cycle, caseData.status, view);
    const steps = stepRows(cycle, logs, view);
    const whatsapp = whatsappRow(cycle, logs);
    const disabled = busyAction !== null;
    const shownFailures = view.tone === "error" || failures.length > 0;

    body = (
      <>
        <div className={`mt-3 rounded-lg border p-3 ${tone.box}`}>
          <p className="flex flex-wrap items-baseline gap-x-1.5 text-xs">
            <span className="text-gray-500">Qui a la main :</span>
            <span className={`inline-flex items-center gap-1.5 font-semibold ${tone.text}`}>
              <span className={`h-2 w-2 flex-none self-center rounded-full ${tone.dot}`} aria-hidden="true" />
              {OWNER_META[owner].label}
            </span>
          </p>
          <div className="[&>div]:bg-white">
            <ReminderProgressBar view={view} />
          </div>
          <p className="mt-2 text-xs font-medium text-gray-800">{nextStepText(cycle, view)}</p>

          {shownFailures && (
            <div role="alert" className="mt-3 border-t border-red-200 pt-2.5">
              <p className="flex items-center gap-1.5 text-xs font-semibold text-red-900">
                <AlertTriangle className="h-3.5 w-3.5" aria-hidden="true" />
                Erreur d’envoi sur ce dossier
              </p>
              <ul className="mt-1 space-y-1 text-xs text-red-800">
                {view.tone === "error" && cycle.last_error && !failures.some((f) => f.error_message === cycle.last_error) && (
                  <li className="break-words">{cycle.last_error}</li>
                )}
                {failures.slice(0, 3).map((failure) => (
                  <li key={failure.id} className="break-words">
                    <span className="font-medium">
                      {failure.channel === "whatsapp" ? "WhatsApp" : "E-mail"} R{failure.reminder_step ?? "?"}
                    </span>{" "}
                    · {formatDateTime(failure.sent_at || failure.created_at)}
                    {failure.error_message ? ` : ${failure.error_message}` : ""}
                  </li>
                ))}
              </ul>
              {actions.canRetry ? (
                <button type="button" disabled={disabled} onClick={() => void act("retry")} className={`${primary} mt-2 w-full`}>
                  <Send className="h-3.5 w-3.5" aria-hidden="true" />
                  {busyAction === "retry" ? "Programmation…" : "Renvoyer"}
                </button>
              ) : (
                <p className="mt-1 text-[11px] text-red-700">Aucun nouvel essai prévu : les relances ne sont plus en cours.</p>
              )}
            </div>
          )}
        </div>

        <div className="mt-3 grid grid-cols-2 gap-2">
          <button type="button" disabled={disabled} onClick={() => void act("call")} className={`${view.tone === "running" || view.tone === "you" ? primary : secondary} ${actions.canResume || actions.canPause ? "" : "col-span-2"}`}>
            <PhoneCall className="h-3.5 w-3.5" aria-hidden="true" />
            {busyAction === "call" ? "Enregistrement…" : "J’ai appelé"}
          </button>
          {actions.canResume ? (
            <button type="button" disabled={disabled} onClick={() => void act("resume")} className={primary}>
              <Play className="h-3.5 w-3.5" aria-hidden="true" />
              {busyAction === "resume" ? "Reprise…" : "Reprendre"}
            </button>
          ) : (
            actions.canPause && (
              <button type="button" disabled={disabled} onClick={() => void act("pause")} className={secondary}>
                <Pause className="h-3.5 w-3.5" aria-hidden="true" />
                {busyAction === "pause" ? "Pause…" : "Mettre en pause"}
              </button>
            )
          )}
          {actions.canSendNext && (
            <button type="button" disabled={disabled} onClick={() => void act("send-now")} className={`${secondary} col-span-2`}>
              <Send className="h-3.5 w-3.5" aria-hidden="true" />
              {busyAction === "send-now" ? "Programmation…" : "Envoyer la relance suivante maintenant"}
            </button>
          )}
        </div>
        <div aria-live="polite" className="min-h-0">
          {actionError ? (
            <p role="alert" className="mt-2 rounded-md bg-red-50 px-2 py-1.5 text-xs text-red-800">{actionError}</p>
          ) : (
            done && !busyAction && <p className="mt-2 text-xs text-green-700">{done}</p>
          )}
        </div>

        <div className="mt-4">
          <h5 className="text-xs font-semibold text-gray-900">Calendrier</h5>
          <ol className="mt-2 space-y-1.5">
            {steps.map((row) => (
              <li key={row.step} className="grid grid-cols-[1.5rem_minmax(0,1fr)] items-start gap-2">
                <span
                  className={`flex h-6 w-6 items-center justify-center rounded-full border-2 text-[11px] font-bold ${STEP_DOT[row.state]}`}
                  aria-hidden="true"
                >
                  {row.step}
                </span>
                <span className="min-w-0">
                  <span className="block text-xs text-gray-900">
                    <span className="font-semibold">{row.label}</span> <span className="text-gray-500">· {row.angle}</span>
                  </span>
                  <span className={`block break-words text-[11px] ${STEP_TEXT[row.state]}`}>{row.text}</span>
                </span>
              </li>
            ))}
            {whatsapp && (
              <li className="grid grid-cols-[1.5rem_minmax(0,1fr)] items-start gap-2">
                <span
                  className={`flex h-6 w-6 items-center justify-center rounded-full ${whatsapp.state === "skipped" ? "bg-gray-100 text-gray-400" : "bg-emerald-100 text-emerald-700"}`}
                  aria-hidden="true"
                >
                  <MessageCircle className="h-3.5 w-3.5" />
                </span>
                <span className="min-w-0">
                  <span className="block text-xs font-semibold text-gray-900">WhatsApp</span>
                  <span className={`block text-[11px] ${whatsapp.state === "skipped" ? "text-gray-400" : "text-emerald-800"}`}>{whatsapp.text}</span>
                  {whatsapp.state === "pending" && (
                    <>
                      <span className="block text-[11px] text-gray-500">Ne part pas si le client a répondu, si les relances sont arrêtées ou hors semaine 9 h – 18 h.</span>
                      {cycle.whatsapp_message && (
                        <details className="mt-1">
                          <summary className="cursor-pointer rounded text-[11px] font-medium text-emerald-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500">
                            Voir le message
                          </summary>
                          <p className="mt-1 whitespace-pre-wrap rounded-md border border-emerald-200 bg-emerald-50 p-2 text-[11px] text-gray-800">{cycle.whatsapp_message}</p>
                        </details>
                      )}
                    </>
                  )}
                </span>
              </li>
            )}
          </ol>
        </div>

        {cycle.rejection_reason && (
          <div className="mt-3 rounded-md border border-red-200 bg-red-50 p-2.5">
            <p className="text-xs font-semibold text-red-900">Motif de refus</p>
            <p className="mt-0.5 text-xs text-red-800">{cycle.rejection_reason}</p>
            {cycle.rejection_comment && <p className="mt-1 text-xs text-red-800">{cycle.rejection_comment}</p>}
          </div>
        )}

        <dl className="mt-4 grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-1 text-[11px]">
          <dt className="text-gray-500">Devis suivi{quotes.length > 1 ? "s" : ""}</dt>
          <dd className="min-w-0 text-gray-800">
            {quotes.length === 0 ? (
              "—"
            ) : (
              quotes.map((quote) => {
                // Comme l'ancienne page /relances : page de signature si un lien existe, sinon le PDF.
                const href = quote.signature_token ? `/sign/${quote.signature_token}` : quote.file_url || null;
                const label = quote.display_name || `Version ${quote.version}`;
                return (
                  <span key={quote.id} className="block truncate">
                    {href ? (
                      <a
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={`Ouvrir ${label} dans un nouvel onglet`}
                        className="text-indigo-700 underline decoration-indigo-300 underline-offset-2 hover:text-indigo-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
                      >
                        {label}
                      </a>
                    ) : (
                      label
                    )}
                    {quote.amount_ht !== null && <span className="text-gray-500"> · {formatMoney(quote.amount_ht)} HT</span>}
                    {quote.signed && <span className="font-medium text-green-700"> · signé</span>}
                  </span>
                );
              })
            )}
          </dd>
          <dt className="text-gray-500">Dernier message client</dt>
          <dd className="text-gray-800">{cycle.last_client_response_at ? formatDateTime(cycle.last_client_response_at) : "jamais"}</dd>
          <dt className="text-gray-500">Dernier contact équipe</dt>
          <dd className="text-gray-800">{formatDateTime(cycle.last_staff_response_at)}</dd>
        </dl>
      </>
    );
  }

  return (
    <section aria-labelledby={titleId} className="border-b border-gray-200 px-4 py-4">
      <SectionTitle
        id={titleId}
        aside={
          view?.callRecommended ? (
            <span className="rounded bg-orange-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-orange-800">Appel recommandé</span>
          ) : null
        }
      >
        Relances devis
      </SectionTitle>
      {body}
      <RulesBanner />
    </section>
  );
}
